import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { avatars } from '@/config/siteImages';

interface CaseStudy {
  name: string;
  business: string;
  location: string;
  amount: string;
  timeline: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  accentColor: string;
  quote: string;
  outcome: string;
  avatar: string;
}

export default function ResultsSection() {
  const { t, i18n } = useTranslation();
  const isEs = i18n.language === 'es';

  const caseStudies: CaseStudy[] = [
    {
      name: t('results_cs_1_name'),
      business: t('results_cs_1_business'),
      location: t('results_cs_1_location'),
      amount: t('results_cs_1_amount'),
      timeline: t('results_cs_1_timeline'),
      icon: 'ri-shopping-bag-3-line',
      iconBg: 'bg-[#F8F3FB]',
      iconColor: 'text-[#4F2D63]',
      accentColor: 'border-l-[#4F2D63]',
      quote: t('results_cs_1_quote'),
      outcome: t('results_cs_1_result'),
      avatar: avatars.small.rs56a,
    },
    {
      name: t('results_cs_2_name'),
      business: t('results_cs_2_business'),
      location: t('results_cs_2_location'),
      amount: t('results_cs_2_amount'),
      timeline: t('results_cs_2_timeline'),
      icon: 'ri-hammer-line',
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-700',
      accentColor: 'border-l-amber-500',
      quote: t('results_cs_2_quote'),
      outcome: t('results_cs_2_result'),
      avatar: avatars.small.rs56b,
    },
    {
      name: t('results_cs_4_name'),
      business: t('results_cs_4_business'),
      location: t('results_cs_4_location'),
      amount: t('results_cs_4_amount'),
      timeline: t('results_cs_4_timeline'),
      icon: 'ri-restaurant-2-line',
      iconBg: 'bg-[#F0FDF9]',
      iconColor: 'text-[#0F766E]',
      accentColor: 'border-l-[#0F766E]',
      quote: t('results_cs_4_quote'),
      outcome: t('results_cs_4_result'),
      avatar: avatars.small.rs56c,
    },
  ];

  const stats = [
    { value: '$47M+', labelKey: isEs ? 'En Financiamiento Guiado' : 'In Funding Guided', icon: 'ri-funds-line' },
    { value: '2,400+', labelKey: isEs ? 'Negocios Apoyados' : 'Businesses Helped', icon: 'ri-group-line' },
    { value: '~18 días', labelKey: isEs ? 'Tiempo Promedio al Financiamiento' : 'Avg. Time to Funding', icon: 'ri-timer-line' },
    { value: '12 mos.', labelKey: isEs ? 'Período al 0% de Interés' : '0% Interest Period', icon: 'ri-percent-line' },
  ];

  return (
    <section className="py-24 bg-[#F8F3FB] px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">
              {isEs ? 'Resultados Documentados' : 'Documented Results'}
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              {isEs ? 'Historias Reales.' : 'Real Stories.'}{' '}
              <span className="text-[#4F2D63]">{isEs ? 'Resultados Reales.' : 'Real Outcomes.'}</span>
            </h2>
            <p className="text-sm text-gray-500 max-w-xl leading-relaxed">
              {isEs
                ? 'Clientes calificados que confiaron en el proceso. Los resultados individuales varían según el perfil y la elegibilidad.'
                : 'Qualified clients who trusted the process. Individual results vary based on profile and eligibility.'}
            </p>
          </div>
          <Link
            to={isEs ? '/es/resultados' : '/results'}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-[#E9D9F2] text-[#4F2D63] font-semibold text-sm hover:border-[#4F2D63] hover:bg-white transition-colors cursor-pointer whitespace-nowrap self-start lg:self-auto"
          >
            {isEs ? 'Ver todas las historias' : 'View all stories'} <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-12">
          {stats.map(({ value, labelKey, icon }) => (
            <div key={value} className="bg-white rounded-xl p-5 border border-[#E9D9F2] flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#F8F3FB] flex-shrink-0">
                <i className={`${icon} text-[#4F2D63] text-base`}></i>
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900 leading-none">{value}</p>
                <p className="text-xs text-gray-500 mt-1 leading-tight">{labelKey}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Case study cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
          {caseStudies.map((cs) => (
            <div
              key={cs.name}
              className={`bg-white rounded-2xl border border-[#E9D9F2] overflow-hidden border-l-4 ${cs.accentColor} flex flex-col`}
            >
              {/* Card header */}
              <div className="p-5 pb-4 border-b border-[#E9D9F2]">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${cs.iconBg} flex-shrink-0`}>
                    <i className={`${cs.icon} ${cs.iconColor} text-base`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{cs.business}</p>
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <i className="ri-map-pin-line text-xs"></i> {cs.location}
                    </p>
                  </div>
                </div>
                {/* Key numbers */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-[#F8F3FB] rounded-lg px-3 py-2 text-center">
                    <p className="text-lg font-bold text-[#4F2D63]">{cs.amount}</p>
                    <p className="text-xs text-gray-500">{isEs ? 'al 0% de interés' : 'at 0% interest'}</p>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg px-3 py-2 text-center">
                    <p className="text-lg font-bold text-amber-700">{cs.timeline}</p>
                    <p className="text-xs text-gray-500">{isEs ? 'al financiamiento' : 'to funding'}</p>
                  </div>
                </div>
              </div>

              {/* Quote */}
              <div className="p-5 flex-1">
                <div className="flex items-center gap-0.5 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>
                  ))}
                </div>
                <blockquote className="text-sm text-gray-600 leading-relaxed italic mb-4">
                  &ldquo;{cs.quote}&rdquo;
                </blockquote>
                <div className="bg-[#F0FDF9] rounded-lg px-3 py-2.5 border border-emerald-100">
                  <p className="text-xs font-semibold text-emerald-700 flex items-start gap-1.5">
                    <i className="ri-check-circle-fill text-sm flex-shrink-0 mt-0.5"></i>
                    {cs.outcome}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-4 border-t border-[#E9D9F2] flex items-center gap-3">
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                  <img src={cs.avatar} alt={cs.name} className="w-full h-full object-cover object-top" />
                </div>
                <p className="text-sm font-semibold text-gray-800">{cs.name}</p>
                <p className="ml-auto text-xs text-gray-400 italic">
                  {isEs ? 'Resultados ind. varían' : 'Ind. results vary'}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="bg-white border border-[#E9D9F2] rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-base font-bold text-gray-900">
              {isEs ? '¿Listo para escribir tu propia historia?' : 'Ready to write your own story?'}
            </p>
            <p className="text-sm text-gray-500 mt-0.5">
              {isEs
                ? 'Revisión de perfil gratuita. Sin compromiso. Respuesta en 48 horas.'
                : 'Free profile review. No commitment. Response within 48 hours.'}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link
              to={isEs ? '/es/resultados' : '/results'}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[#E9D9F2] text-gray-700 font-semibold text-sm hover:border-[#4F2D63] transition-colors cursor-pointer whitespace-nowrap"
            >
              {isEs ? 'Ver más' : 'See more'}
            </Link>
            <Link
              to={isEs ? '/es/contacto' : '/contact'}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#4F2D63] text-white font-semibold text-sm hover:bg-[#3E234F] transition-colors cursor-pointer whitespace-nowrap"
            >
              {isEs ? 'Solicitar ahora' : 'Apply now'} <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <p className="text-xs text-gray-400 italic text-center mt-4">
          {isEs
            ? 'Los resultados individuales pueden variar. La aprobación está sujeta a elegibilidad y requisitos del prestamista. Los resultados no están garantizados.'
            : 'Individual results may vary. Approval subject to eligibility and lender requirements. Results not guaranteed.'}
        </p>
      </div>
    </section>
  );
}
